import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  MdAdd, MdRefresh, MdContentCopy, MdCancel, MdHistory, MdAlarm, MdDelete,
} from 'react-icons/md';
import client from '../api/client';
import { erroriPerCampo, messaggioErrore } from '../utils/apiError';
import {
  Badge, Button, Checkbox, ConfirmDialog, DialogBody, DialogFooter, Drawer, EmptyState, Field,
  FilterChip, Modal, PageHeader, SkeletonList, Toolbar,
} from '../components/ui';
import { etichettaStato } from '../components/StatusBadge';
import PersonPicker from '../components/address/PersonPicker';

const TONO = {
  scheduled: 'info', queued: 'warning', sending: 'info', sent: 'info',
  completed: 'success', delivered: 'success', read: 'success',
  stopped: 'neutral', cancelled: 'neutral',
  missed: 'warning', unknown: 'warning', failed: 'danger', pending: 'warning',
};

const FILTRI = [
  { id: 'attivi', etichetta: 'In corso', stati: ['scheduled', 'queued', 'sending', 'pending'] },
  { id: 'conclusi', etichetta: 'Conclusi', stati: ['completed', 'stopped', 'sent'] },
  { id: 'problemi', etichetta: 'Da controllare', stati: ['missed', 'unknown', 'failed', 'cancelled'] },
];

// Con invii ancora in programma un promemoria si annulla; finito, si può eliminare (04/10/2026).
const ATTIVI = ['scheduled', 'queued', 'sending', 'pending'];
const attivo = item => item.recipients.some(r => ATTIVI.includes(r.status));

const FUSI = (() => {
  try { return Intl.supportedValuesOf('timeZone'); }
  catch { return ['Europe/Rome', 'UTC']; }
})();

const vuoto = {
  connection: '', contacts: [], title: '', body: '', starts_local: '', tzid: 'Europe/Rome',
  fold: 0, interval_minutes: 60, max_attempts: 1, grace_minutes: 60,
  stop_on_reply: true, stop_on_reaction: false, stop_on_read: false,
};

const quando = (valore, tzid) => (valore
  ? new Date(valore).toLocaleString('it-IT', { timeZone: tzid })
  : '—');

export default function RemindersPage() {
  const [items, setItems] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [options, setOptions] = useState({ contacts: [], engine_enabled: false });
  const [caricamento, setCaricamento] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState('');
  const [erroriCampo, setErroriCampo] = useState({});
  const [filtri, setFiltri] = useState([]);
  const [bozza, setBozza] = useState(null);
  const [storico, setStorico] = useState(null);
  const [daEliminare, setDaEliminare] = useState(null); // un promemoria, o l'elenco dei finiti
  const chiave = useRef(null);

  const ricarica = useCallback(async () => {
    setCaricamento(true);
    try {
      const { data } = await client.get('/v1/reminders/');
      setItems(data);
    } catch (e) {
      setErrore(messaggioErrore(e));
    } finally {
      setCaricamento(false);
    }
  }, []);

  useEffect(() => {
    ricarica();
    client.get('/v1/sessions/')
      .then(({ data }) => setSessions(data.filter(s => !s.is_deleted)))
      .catch(e => setErrore(messaggioErrore(e)));
  }, [ricarica]);

  useEffect(() => {
    let vivo = true;
    setOptions({ contacts: [], engine_enabled: false });
    if (bozza?.connection) {
      client.get('/v1/reminders/options/', { params: { connection: bozza.connection } })
        .then(({ data }) => { if (vivo) setOptions(data); })
        .catch(e => { if (vivo) setErrore(messaggioErrore(e)); });
    }
    return () => { vivo = false; };
  }, [bozza?.connection]);

  // Il selettore a ricerca e chip vuole persone: i contatti si adattano.
  const persone = useMemo(() => options.contacts.map(c => ({
    id: c.id,
    display_name: c.name || c.phone_number || c.whatsapp_id,
    short_name: '',
    category: '',
    identities: [c],
  })), [options.contacts]);

  const visibili = useMemo(() => {
    if (!filtri.length) return items;
    const stati = new Set(FILTRI.filter(f => filtri.includes(f.id)).flatMap(f => f.stati));
    return items.filter(item => item.recipients.some(r => stati.has(r.status)));
  }, [items, filtri]);

  const crea = async () => {
    setBusy(true); setErrore(''); setErroriCampo({});
    chiave.current = chiave.current || crypto.randomUUID();
    try {
      await client.post('/v1/reminders/', { ...bozza, idempotency_key: chiave.current });
      chiave.current = null;
      toast.success('Promemoria creato.');
      setBozza(null);
      await ricarica();
    } catch (e) {
      // Il modulo resta aperto: quello che hai scritto non si perde.
      setErroriCampo(erroriPerCampo(e));
      setErrore(messaggioErrore(e));
    } finally {
      setBusy(false);
    }
  };

  const annulla = async (item, destinatario) => {
    setBusy(true);
    try {
      const { data } = await client.post(`/v1/reminders/${item.id}/cancel/`,
        destinatario ? { recipient: destinatario.id, version: destinatario.version } : {});
      toast.info(data.notice);
      await ricarica();
    } catch (e) {
      toast.error(messaggioErrore(e));
    } finally {
      setBusy(false);
    }
  };

  const finiti = items.filter(item => !attivo(item));
  const elimina = async () => {
    const elenco = Array.isArray(daEliminare) ? daEliminare : [daEliminare];
    setBusy(true);
    let fatti = 0;
    try {
      for (const item of elenco) {
        await client.delete(`/v1/reminders/${item.id}/`);
        fatti += 1;
      }
      toast.success(fatti === 1 ? 'Promemoria eliminato.' : `${fatti} promemoria eliminati.`);
    } catch (e) {
      toast.error(messaggioErrore(e));
    } finally {
      setBusy(false);
      setDaEliminare(null);
      await ricarica();
    }
  };

  const duplica = item => setBozza({
    ...vuoto,
    connection: String(item.connection),
    title: item.title,
    body: item.body,
    tzid: item.tzid,
    interval_minutes: item.interval_minutes,
    max_attempts: item.max_attempts,
    grace_minutes: item.grace_minutes,
    stop_on_reply: item.stop_on_reply,
    stop_on_reaction: item.stop_on_reaction,
    stop_on_read: item.stop_on_read,
  });

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <PageHeader title="Promemoria"
        subtitle="Un testo programmato per una o più persone. Ogni destinatario ha le sue ripetizioni e si ferma per conto suo."
        actions={(
          <>
            <Button icon={MdRefresh} onClick={ricarica} loading={caricamento}>Aggiorna</Button>
            <Button variant="primary" icon={MdAdd} onClick={() => setBozza({ ...vuoto })}>
              Nuovo promemoria
            </Button>
          </>
        )} />

      {errore && !bozza && (
        <p role="alert" className="rounded bg-danger-bg px-3 py-2 text-sm text-danger">{errore}</p>
      )}

      <Toolbar>
        {FILTRI.map(f => (
          <FilterChip key={f.id} active={filtri.includes(f.id)}
            onClick={() => setFiltri(filtri.includes(f.id)
              ? filtri.filter(x => x !== f.id) : [...filtri, f.id])}>
            {f.etichetta}
          </FilterChip>
        ))}
        {finiti.length > 1 && (
          <Button size="sm" variant="danger-ghost" icon={MdDelete} disabled={busy}
            onClick={() => setDaEliminare(finiti)}>Elimina i finiti ({finiti.length})</Button>
        )}
      </Toolbar>

      {caricamento ? <SkeletonList rows={3} /> : !visibili.length ? (
        <EmptyState icon={MdAlarm}
          title={items.length ? 'Nessun promemoria con questi filtri' : 'Nessun promemoria'}
          hint="Programma un messaggio e lascia che si ripeta finché la persona non risponde."
          action={<Button variant="primary" icon={MdAdd} onClick={() => setBozza({ ...vuoto })}>Nuovo promemoria</Button>} />
      ) : (
        <ul className="space-y-3">
          {visibili.map(item => (
            <li key={item.id} className="space-y-3 rounded-lg border border-line bg-surface p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-ink">{item.title}</h2>
                  <p className="mt-0.5 whitespace-pre-wrap text-sm text-muted">{item.body}</p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" icon={MdContentCopy} onClick={() => duplica(item)}>Duplica</Button>
                  {attivo(item) ? (
                    <Button size="sm" variant="danger-ghost" icon={MdCancel} disabled={busy}
                      onClick={() => annulla(item)}>Annulla per tutti</Button>
                  ) : (
                    <Button size="sm" variant="danger-ghost" icon={MdDelete} disabled={busy}
                      onClick={() => setDaEliminare(item)}>Elimina</Button>
                  )}
                </div>
              </div>

              <p className="text-xs text-muted">
                Dal {quando(item.starts_at, item.tzid)} · {item.tzid} · massimo {item.max_attempts} invii,
                ogni {item.interval_minutes} minuti
              </p>
              {!item.engine_enabled && (
                <p className="rounded bg-warning-bg px-2 py-1 text-xs text-warning">
                  Invii sospesi: questo account non è abilitato al nuovo motore.
                </p>
              )}

              <ul className="divide-y divide-line border-t border-line">
                {item.recipients.map(target => (
                  <li key={target.id} className="flex flex-wrap items-center gap-2 py-2">
                    <strong className="text-sm text-ink">{target.name || target.chat_id}</strong>
                    <Badge tone={TONO[target.status] || 'neutral'}>{etichettaStato(target.status)}</Badge>
                    <span className="text-xs tabular-nums text-muted">
                      {target.attempts}/{item.max_attempts}
                    </span>
                    {target.next_due_at && (
                      <span className="text-xs text-muted">
                        prossimo {quando(target.next_due_at, item.tzid)}
                      </span>
                    )}
                    <span className="flex-1" />
                    {target.deliveries.length > 0 && (
                      <Button size="sm" icon={MdHistory}
                        onClick={() => setStorico({ item, target })}>Storico</Button>
                    )}
                    {['scheduled', 'queued'].includes(target.status) && (
                      <Button size="sm" variant="danger-ghost" disabled={busy}
                        onClick={() => annulla(item, target)}>Annulla</Button>
                    )}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-muted">
        Un invio già in corso può arrivare anche dopo l&apos;annullamento. Una risposta non citata
        non ferma il promemoria; la lettura dipende dalle conferme che WhatsApp rende disponibili.
      </p>

      <Modal open={!!bozza} onClose={() => { setBozza(null); setErroriCampo({}); }} size="lg"
        label="Nuovo promemoria">
        <DialogBody className="space-y-3">
          {errore && <p role="alert" className="rounded bg-danger-bg px-3 py-2 text-sm text-danger">{errore}</p>}

          <Field label="Account WhatsApp" required error={erroriCampo.connection}>
            {props => (
              <select {...props} value={bozza?.connection || ''}
                onChange={e => setBozza({ ...bozza, connection: e.target.value, contacts: [] })}>
                <option value="">Scegli account</option>
                {sessions.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.phone_number || s.name || s.session_id} · {etichettaStato(s.status)}
                  </option>
                ))}
              </select>
            )}
          </Field>

          {bozza?.connection && !options.engine_enabled && (
            <p className="rounded bg-warning-bg px-3 py-2 text-sm text-warning">
              Il nuovo motore non è attivo su questo account: puoi salvare, ma non partirà.
            </p>
          )}

          {bozza?.connection && (
            <PersonPicker label="Destinatari" max={20} people={persone}
              selected={bozza.contacts} onChange={contacts => setBozza({ ...bozza, contacts })}
              hint="Solo chat private di questo account. Cerca e spunta: niente Ctrl o Cmd." />
          )}
          {erroriCampo.contacts && <p role="alert" className="text-xs text-danger">{erroriCampo.contacts}</p>}

          <Field label="Titolo" required error={erroriCampo.title} value={bozza?.title || ''}
            onChange={e => setBozza({ ...bozza, title: e.target.value })} />
          <Field label="Testo da inviare" required error={erroriCampo.body}>
            {props => (
              <textarea {...props} rows={4} maxLength={10000} value={bozza?.body || ''}
                onChange={e => setBozza({ ...bozza, body: e.target.value })} />
            )}
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Primo invio" required type="datetime-local" error={erroriCampo.starts_local}
              value={bozza?.starts_local || ''}
              onChange={e => setBozza({ ...bozza, starts_local: e.target.value })} />
            <Field label="Fuso orario" required error={erroriCampo.tzid}>
              {props => (
                <select {...props} value={bozza?.tzid || 'Europe/Rome'}
                  onChange={e => setBozza({ ...bozza, tzid: e.target.value })}>
                  {FUSI.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              )}
            </Field>
            <Field label="Invii massimi per persona" required type="number" min={1} max={100}
              value={bozza?.max_attempts ?? 1} error={erroriCampo.max_attempts}
              onChange={e => setBozza({ ...bozza, max_attempts: Number(e.target.value) })} />
            <Field label="Intervallo tra invii (minuti)" required type="number" min={5} max={43200}
              value={bozza?.interval_minutes ?? 60} error={erroriCampo.interval_minutes}
              onChange={e => setBozza({ ...bozza, interval_minutes: Number(e.target.value) })} />
            <Field label="Tolleranza per ritardi (minuti)" required type="number" min={1} max={1440}
              value={bozza?.grace_minutes ?? 60} error={erroriCampo.grace_minutes}
              hint="Oltre questa attesa l'invio risulta scaduto invece di partire in ritardo."
              onChange={e => setBozza({ ...bozza, grace_minutes: Number(e.target.value) })} />
          </div>

          <fieldset className="space-y-2 rounded-lg border border-line p-3">
            <legend className="px-1 text-sm font-medium text-ink">Ferma le ripetizioni quando</legend>
            {[['stop_on_reply', 'Risponde citando uno degli invii'],
              ['stop_on_reaction', 'Aggiunge una reazione a uno degli invii'],
              ['stop_on_read', 'Arriva la conferma di lettura']].map(([campo, etichetta]) => (
              <Checkbox key={campo} label={etichetta} checked={bozza?.[campo] || false}
                onChange={e => setBozza({ ...bozza, [campo]: e.target.checked })} />
            ))}
            <p className="text-xs text-muted">
              Vale per la singola persona: se risponde Anna, i promemoria a Luca continuano.
            </p>
          </fieldset>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={() => { setBozza(null); setErroriCampo({}); }}>Annulla</Button>
          <Button variant="primary" loading={busy} disabled={!bozza?.contacts?.length} onClick={crea}>
            Crea promemoria
          </Button>
        </DialogFooter>
      </Modal>

      <ConfirmDialog open={!!daEliminare} onClose={() => setDaEliminare(null)} onConfirm={elimina} danger loading={busy}
        title={Array.isArray(daEliminare) ? `Eliminare ${daEliminare.length} promemoria finiti?` : `Eliminare «${daEliminare?.title || ''}»?`}
        confirmLabel="Elimina"
        message={`${Array.isArray(daEliminare) ? 'Spariscono dall’elenco con il loro storico' : 'Sparisce dall’elenco con il suo storico'}. I messaggi già mandati restano nelle chat di WhatsApp.`} />

      <Drawer open={!!storico} onClose={() => setStorico(null)}
        label={`Storico invii: ${storico?.target.name || storico?.target.chat_id || ''}`}
        description={storico?.item.title}>
        <DialogBody>
          <ol className="space-y-2">
            {storico?.target.deliveries.map(d => (
              <li key={d.attempt} className="rounded border border-line p-2 text-sm">
                <p className="flex flex-wrap items-center gap-2">
                  <strong className="text-ink">Invio #{d.attempt}</strong>
                  <Badge tone={TONO[d.status] || 'neutral'}>{etichettaStato(d.status)}</Badge>
                </p>
                <p className="text-xs text-muted">{quando(d.sent_at, storico.item.tzid)}</p>
                {d.error && <p className="mt-1 text-xs text-danger">{d.error}</p>}
              </li>
            ))}
          </ol>
        </DialogBody>
        <DialogFooter>
          <Button variant="primary" onClick={() => setStorico(null)}>Chiudi</Button>
        </DialogFooter>
      </Drawer>
    </div>
  );
}
